// Explore › Powers of Ten, the view (features/journey.ts runs it): the controller that starts, stops, resumes and ends the
// viewport's journey with the view; the scale ruler on the right edge of the picture (vertical, log, from the whole brain
// to one neuron, the stages as stops and a marker riding the engine's scale events); the transport's Look around; the
// panel's section (the stage on screen and its Methods line, the stages to jump to, the streaming note); and the Methods
// sheet's section. Spec: docs/areas/web-powers-journey.md.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Orbit, Route } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { journeyOf } from '../app/scene.ts';
import { on } from '../viewport/events.ts';
import { getViewport } from '../viewport/host.ts';
import { Button, IconButton, Section } from '../ui/primitives.tsx';
import {
  asReply,
  formatScale,
  JOURNEY_METHODS,
  JOURNEY_STREAM_NOTE,
  journeyActive,
  journeyName,
  journeyStatusLine,
  lastJourney,
  namedStops,
  provenanceOf,
  reason,
  resetJourney,
  rulerDecades,
  rulerFrac,
  scaleAt,
  stageAt,
  UNAVAILABLE_BUILD,
  UNAVAILABLE_PREVIEW,
  useJourney,
} from './journey.ts';
import type { JourneyStage } from '../viewport/api.ts';
import './web.css';

export { journeyActive };

/** The journey's life with the view: on once the brain is in and the viewport lists it, off when the view closes. */
function useJourneyController() {
  const id = useAtlas((s) => journeyOf(s)?.id ?? null);
  const duration = useAtlas((s) => journeyOf(s)?.duration ?? 0);
  const engineStatus = useAtlas((s) => s.engine.status);
  const kind = useAtlas((s) => s.engine.kind);
  const journeys = useAtlas((s) => s.engine.journeys?.join(' ') ?? null);
  const loadStatus = useAtlas((s) => s.load.status);
  const packId = useAtlas((s) => s.packId);
  const status = useJourney((s) => s.status);
  const playing = useAtlas((s) => s.playing);

  // Start and end.
  useEffect(() => {
    if (!id) {
      resetJourney();
      useJourney.getState().setOpenCamera(null);
      return;
    }
    const name = journeyName(id);
    if (engineStatus !== 'ready' || loadStatus !== 'ready') {
      useJourney.setState({ id, name, status: 'waiting', why: null });
      return;
    }
    if (journeys !== null && !journeys.split(' ').includes(id)) {
      useJourney.setState({ id, name, status: 'unavailable', why: kind === 'mock' ? UNAVAILABLE_PREVIEW : UNAVAILABLE_BUILD });
      return;
    }
    let live = true;
    let started = false;
    void (async () => {
      const vp = await getViewport();
      if (!vp || !live) return;
      const a = useAtlas.getState();
      // The clock waits while the first stage streams, then goes on from the link's moment.
      const from = Math.min(Math.max(0, a.time), duration || Infinity);
      a.setPlaying(false);
      useJourney.setState({ id, name, status: 'starting', why: null, progress: null, stage: null, stoppedAt: null, reply: null });
      try {
        const r = await vp.send({ cmd: 'journey', id, on: true, from });
        if (!live) {
          void vp.send({ cmd: 'journey', id, on: false }).catch(() => undefined);
          return;
        }
        started = true;
        const reply = asReply(r);
        useAtlas.getState().setJourney({ id, ...reply });
        useJourney.setState({ status: 'on', reply, progress: null, stage: stageAt(reply, from) });
        const cam = useJourney.getState().openCamera;
        if (cam) {
          // A link's camera: stopped where the link was, looking around there.
          useJourney.getState().setOpenCamera(null);
          await useJourney.getState().stop();
          if (live && useJourney.getState().status === 'stopped') useAtlas.getState().camera({ orbit: cam, transitionMs: 0 });
        } else {
          useAtlas.getState().setTime(from);
          useAtlas.getState().setPlaying(true);
        }
      } catch (err) {
        if (live) useJourney.setState({ status: 'error', why: reason(err), progress: null });
      }
    })();
    return () => {
      live = false;
      // The view closed (or the brain reloads, which ends it in the viewport too): the scene underneath draws again.
      if (started) void getViewport()?.then((vp) => vp.send({ cmd: 'journey', id, on: false })).catch(() => undefined);
      const a = useAtlas.getState();
      a.setJourney(null);
      if (a.playing) a.setPlaying(false);
      resetJourney();
    };
  }, [id, duration, engineStatus, kind, journeys, loadStatus, packId]);

  // Its events: the marker reads them itself; the stage goes to the store when it changes.
  useEffect(
    () =>
      on('journey', (e) => {
        lastJourney.current = e;
        if (useJourney.getState().stage !== e.stage) useJourney.setState({ stage: e.stage });
      }),
    [],
  );
  useEffect(
    () =>
      on('progress', (e) => {
        if (e.of !== 'journey') return;
        useJourney.setState({ progress: e.total > 0 && e.loaded < e.total ? { loaded: e.loaded, total: e.total } : null });
      }),
    [],
  );

  // Stopped: Play, or a scrub of the track, means back to the path (from that moment).
  useEffect(() => {
    if (status !== 'stopped') return;
    const offTime = useAtlas.subscribe(
      (s) => s.time,
      (t) => void useJourney.getState().resume(t),
    );
    const offPlay = useAtlas.subscribe(
      (s) => s.playing,
      (p) => {
        if (p) void useJourney.getState().resume();
      },
    );
    return () => {
      offTime();
      offPlay();
    };
  }, [status]);

  // On the path, a press on the picture stops to look around: the viewport ignores the pointer while it plays.
  useEffect(() => {
    if (status !== 'on') return;
    const canvas = document.querySelector<HTMLCanvasElement>('canvas.viewport');
    if (!canvas) return;
    const down = (e: PointerEvent) => {
      if (e.button !== 0 || !e.isPrimary) return;
      void useJourney.getState().stop();
    };
    canvas.addEventListener('pointerdown', down);
    return () => canvas.removeEventListener('pointerdown', down);
  }, [status, playing]);
}

/** Mounted once in the app: the controller, and the ruler while the view is on. */
export function Journey() {
  useJourneyController();
  const active = useAtlas(journeyActive);
  const running = useJourney((s) => s.status === 'starting' || s.status === 'on' || s.status === 'stopped');
  return active && running ? <ScaleRuler /> : null;
}

/**
 * The scale ruler (spec §2): vertical, log, from the whole brain's view (about 200 mm) to one neuron's (0.7 mm), on
 * the right edge of the picture; the decades ticked, the stages as stops (click one to play from its start), and a
 * marker riding the view's height as the engine reports it, at most once a frame. Stopped, the marker stays put.
 */
function ScaleRuler() {
  const reply = useJourney((s) => s.reply);
  const status = useJourney((s) => s.status);
  const stage = useJourney((s) => s.stage);
  const stoppedAt = useJourney((s) => s.stoppedAt);
  const jumpTo = useJourney((s) => s.jumpTo);
  const insets = useAtlas((s) => s.insets);
  const root = useRef<HTMLElement>(null);
  const marker = useRef<HTMLDivElement>(null);
  const text = useRef<HTMLSpanElement>(null);
  const ticks = useRef(new Map<number, HTMLDivElement>());
  const [height, setHeight] = useState(0);
  const stages = reply?.stages ?? [];
  const hi = stages[0]?.scaleMm[0] ?? 200;
  const lo = stages.at(-1)?.scaleMm[1] ?? 0.5;
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeight(el.clientHeight));
    ro.observe(el);
    setHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const show = (scaleMm: number) => {
      if (!marker.current || !text.current) return;
      const f = rulerFrac(scaleMm, hi, lo);
      marker.current.style.top = `${(f * 100).toFixed(2)}%`;
      marker.current.hidden = false;
      text.current.textContent = formatScale(scaleMm);
      // A decade's label gives way while the marker's reading passes it: both sit just right of the rail.
      for (const [v, el] of ticks.current) el.dataset.near = String(Math.abs(rulerFrac(v, hi, lo) - f) * height < 14);
    };
    // Stopped before any event has come (a link's camera): the reply says the scale at that moment.
    const stopped = stoppedAt !== null ? scaleAt(reply, stoppedAt) : null;
    if (lastJourney.current) show(lastJourney.current.scaleMm);
    else if (stopped !== null) show(stopped);
    return on('journey', (e) => show(e.scaleMm));
  }, [hi, lo, reply, stoppedAt, height]);
  const named = namedStops(stages, hi, lo, height, stage);
  const streaming = status === 'waiting' || status === 'starting';
  return (
    <aside
      ref={root}
      className="ruler"
      data-inset="right"
      data-streaming={streaming}
      aria-label="Scale: the view’s height at the camera’s target, from a head to one receptor"
      style={{ right: insets.right + 20, top: insets.top + 28, bottom: insets.bottom + 28 }}
    >
      <div className="ruler-rail" />
      {rulerDecades(hi, lo).map((v) => (
        <div
          key={v}
          ref={(el) => void (el ? ticks.current.set(v, el) : ticks.current.delete(v))}
          className="ruler-tick"
          style={{ top: `${(rulerFrac(v, hi, lo) * 100).toFixed(2)}%` }}
        >
          <span className="num">{formatScale(v)}</span>
        </div>
      ))}
      {stages.map((st) => (
        <button
          key={st.id}
          type="button"
          className={`ruler-stop ${stage === st.id ? 'on' : ''} ${named.has(st.id) ? '' : 'dot'}`}
          style={{ top: `${(rulerFrac(st.scaleMm[0], hi, lo) * 100).toFixed(2)}%` }}
          aria-label={`${st.name}: ${formatScale(st.scaleMm[0])} to ${formatScale(st.scaleMm[1])}, from ${st.t0} s`}
          aria-current={stage === st.id ? 'step' : undefined}
          title={`${st.name} · ${formatScale(st.scaleMm[0])} → ${formatScale(st.scaleMm[1])}`}
          onClick={() => jumpTo(st.t0)}
        >
          <span className="ruler-stop-name">{st.name}</span>
          <span className="ruler-stop-dot" aria-hidden="true" />
        </button>
      ))}
      <div ref={marker} className="ruler-marker" hidden aria-live="off">
        <span ref={text} className="ruler-marker-text num" />
      </div>
    </aside>
  );
}

/** The transport's Look around (spec §4): stop where the path is, the camera yours; again, back to the path and on. */
export function JourneyTransport() {
  const active = useAtlas(journeyActive);
  const status = useJourney((s) => s.status);
  const toggleLook = useJourney((s) => s.toggleLook);
  if (!active) return null;
  const stopped = status === 'stopped';
  return (
    <IconButton
      className="tl-look"
      icon={stopped ? Route : Orbit}
      label={stopped ? 'Back to the path' : 'Look around'}
      active={stopped}
      tipSide="top"
      disabled={status !== 'on' && status !== 'stopped'}
      onClick={toggleLook}
    />
  );
}

const span = (st: JourneyStage) => `${st.t0}–${st.t1} s`;

/** The panel's section: what's on screen and why, the stages, Look around, and what a first play streams. */
export function JourneySections() {
  const active = useAtlas(journeyActive);
  const status = useJourney((s) => s.status);
  const why = useJourney((s) => s.why);
  const progress = useJourney((s) => s.progress);
  const name = useJourney((s) => s.name);
  const reply = useJourney((s) => s.reply);
  const stageId = useJourney((s) => s.stage);
  const toggleLook = useJourney((s) => s.toggleLook);
  const jumpTo = useJourney((s) => s.jumpTo);
  if (!active) return null;
  const st = reply?.stages.find((x) => x.id === stageId) ?? null;
  const line = journeyStatusLine({ status, why, progress, name });
  const stopped = status === 'stopped';
  return (
    <Section title={name ?? 'Powers of Ten'}>
      {line ? (
        <p className={`journey-status ba-type-caption ${status === 'error' ? 'error' : ''}`} role="status">
          {line}
        </p>
      ) : null}
      {st ? (
        <>
          <div className="journey-stage">
            <span className="journey-stage-name ba-type-body-strong">{st.name}</span>
            <span className="journey-stage-scale ba-type-caption num">{`${formatScale(st.scaleMm[0])} → ${formatScale(st.scaleMm[1])}`}</span>
          </div>
          <p className="journey-methods ba-type-caption">{st.methods}</p>
        </>
      ) : null}
      {status === 'on' || stopped ? (
        <>
          <div className="journey-actions">
            <Button icon={stopped ? Route : Orbit} aria-pressed={stopped} onClick={toggleLook}>
              {stopped ? 'Back to the path' : 'Look around'}
            </Button>
          </div>
          <p className="journey-hint ba-type-caption">
            {stopped ? 'The camera is yours: drag to orbit, scroll to zoom; on H01, click a cell. Play, or scrub, goes back to the path.' : 'Press on the picture, or Look around, to orbit where the path has left you.'}
          </p>
        </>
      ) : null}
      {reply ? (
        <ul className="journey-stages" aria-label="Stages">
          {reply.stages.map((x) => (
            <li key={x.id}>
              <button type="button" className="journey-stop ba-type-caption" aria-current={x.id === stageId ? 'true' : undefined} onClick={() => jumpTo(x.t0)}>
                <span>{x.name}</span>
                <span className="num">{span(x)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {status !== 'unavailable' && status !== 'error' ? <p className="journey-note ba-type-caption">{JOURNEY_STREAM_NOTE}</p> : null}
    </Section>
  );
}

/** The Methods sheet's section: the dive, the stage on screen, every stage's line, and what it streams. */
export function JourneyMethods() {
  const reply = useJourney((s) => s.reply);
  const stageId = useJourney((s) => s.stage);
  const st = reply?.stages.find((x) => x.id === stageId) ?? null;
  return (
    <section className="methods-sim methods-journey">
      <h5 className="caps">Powers of Ten</h5>
      <dl>
        {st ? (
          <>
            <dt>On screen</dt>
            <dd>
              {`${st.name}, ${formatScale(st.scaleMm[0])} to ${formatScale(st.scaleMm[1])}, ${span(st)}. `}
              {st.methods}
            </dd>
          </>
        ) : null}
        {reply ? (
          <>
            <dt>Stages</dt>
            <dd>
              <ul className="methods-inputs methods-stages">
                {reply.stages.map((x) => (
                  <li key={x.id}>
                    <span>{x.name}</span>
                    <span className="muted num">{` · ${span(x)} · ${formatScale(x.scaleMm[0])} → ${formatScale(x.scaleMm[1])}`}</span>
                    {provenanceOf(x.methods) ? <span className="muted">{` · ${provenanceOf(x.methods)}`}</span> : null}
                  </li>
                ))}
              </ul>
            </dd>
            <dt>Scale</dt>
            <dd>{reply.scale || 'The view’s height at the camera’s target, in mm'}</dd>
            <dt>Streams</dt>
            <dd>
              {reply.data.map((d) => (
                <div key={d} className="mono">
                  {d}
                </div>
              ))}
              <div className="muted">{JOURNEY_STREAM_NOTE}</div>
            </dd>
          </>
        ) : (
          <>
            <dt>Streams</dt>
            <dd className="muted">{JOURNEY_STREAM_NOTE}</dd>
          </>
        )}
        <dt>Engine</dt>
        <dd>
          <span className="mono">journey powers-of-ten</span>
          <span className="muted">{' · engines/wgpu #778 · viewport API 0.3'}</span>
        </dd>
      </dl>
    </section>
  );
}

export { JOURNEY_METHODS };
